import { createApp } from 'vue';
import { registerPageInit } from '../barba/page-init-registry';
import '../../css/pages/home/index.scss';
import gsap from 'gsap';
import { HomeClustersSwiperWidget } from '@/js/widgets/home-clusters-swiper-widget';
import { HomeResidentsSwiperWidget } from '@/js/widgets/home-residents-swiper-widget';
import { HomeNewResidentSwiperWidget } from '@/js/widgets/home-new-resident-swiper-widget';
import { ResidentModalWidget } from '@/js/widgets/resident-modal-widget';
import { AdvantagesAnimationConfig } from '@/js/utils/animations/advantages-animation-config';
import { initHomeAdvantagesVideo } from '@/js/utils/home-advantages-video';
import { sanitizeInlineSvgRoot } from '@/js/utils/sanitize-inline-svg';
import InteractiveMap from '@/js/widgets/interactive-map/interactive-map.vue';

function initInteractiveMap(): void {
  const el = document.getElementById('interactive-map-vue');
  if (!el) return;

  const mapHref = (el as HTMLElement).dataset.mapHref?.trim() ?? '';

  createApp(InteractiveMap, { mapHref }).mount(el);
}

class Index {
  private carousel: Element | null;
  private indicators: NodeListOf<HTMLElement>;
  private homeClustersSwiperWidget: HomeClustersSwiperWidget;
  private homeResidentsSwiperWidget: HomeResidentsSwiperWidget;
  private homeNewResidentSwiperWidget: HomeNewResidentSwiperWidget;
  private residentModalWidget: ResidentModalWidget;
  private counters: HTMLElement[] = [];
  private animationObserver: IntersectionObserver | null = null;
  private advantagesSection: HTMLElement | null = null;
  private hasAnimatedAdvantages = false;

  constructor() {
    this.carousel = document.getElementById('residentsCarousel');
    // Селектор остается только для резидентов
    this.indicators = document.querySelectorAll(
      '#residentsCarousel ~ .carousel-navigation .carousel-indicators-custom button',
    );
    this.homeClustersSwiperWidget = new HomeClustersSwiperWidget();
    this.homeResidentsSwiperWidget = new HomeResidentsSwiperWidget();
    this.homeNewResidentSwiperWidget = new HomeNewResidentSwiperWidget();
    this.residentModalWidget = new ResidentModalWidget();
  }

  public init(root?: ParentNode): void {
    if (!root) {
      console.info('no root');
    }
    initInteractiveMap();
    this.bindEvents();
    this.homeClustersSwiperWidget.init();
    this.homeResidentsSwiperWidget.init();
    this.homeNewResidentSwiperWidget.init();
    this.residentModalWidget.init();

    this.counters = Array.from(document.querySelectorAll<HTMLElement>('.counter-animation'));
    if (this.counters.length) {
      this.initCounterAnimation();
    }

    this.initAdvantagesAnimation();
    this.initHomeAdvantagesVideo();
    void this.injectHomeAdvantageInlineSvgs().then(() => this.initAdvantageIconsStrokeDraw());
    this.initHomePromoTitleWords();
    this.initCursorTrailSparkles();
  }

  private initCounterAnimation(): void {
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const el = entry.target as HTMLElement;
          this.animateCounter(el);

          // анимация только один раз
          obs.unobserve(el);
        });
      },
      {
        threshold: 1, // 100% элемента видно
        rootMargin: '0px 0px 5% 0px',
      },
    );

    this.counters.forEach((counter) => observer.observe(counter));
  }

  private animateCounter(el: HTMLElement): void {
    const rawValue = el.dataset.value;
    if (!rawValue) return;

    const targetValue = Number(rawValue);
    if (Number.isNaN(targetValue)) return;

    // определяем количество знаков после запятой
    const decimals = rawValue.includes('.') ? rawValue.split('.')[1].length : 0;

    const obj = { value: 0 };

    gsap.to(obj, {
      value: targetValue,
      duration: 2,
      ease: 'power1.out',
      onUpdate: () => {
        el.textContent = obj.value
          .toLocaleString('ru-RU', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          })
          .replace(',', '.');
      },
    });
  }

  private async injectHomeAdvantageInlineSvgs(): Promise<void> {
    const boxes = document.querySelectorAll<HTMLElement>(
      '.home-advantages [data-advantage-icon][data-inline-svg]',
    );

    await Promise.all(
      Array.from(boxes).map(async (box) => {
        if (box.dataset.inlineSvgInjected === 'true') return;
        if (box.querySelector(':scope > svg')) {
          box.dataset.inlineSvgInjected = 'true';
          return;
        }

        const img = box.querySelector<HTMLImageElement>(
          ':scope > img.home-advantages__icon-img, :scope > img',
        );
        if (!img) return;

        const src = img.getAttribute('src')?.trim();
        if (!src) {
          box.classList.add('home-advantages__img-box--icon-fallback');
          return;
        }

        let injected = false;
        try {
          const response = await fetch(src, { credentials: 'same-origin' });
          if (!response.ok) return;

          const text = await response.text();
          const parsed = new DOMParser().parseFromString(text, 'image/svg+xml');
          if (parsed.querySelector('parsererror')) return;

          const root = parsed.documentElement;
          if (!root || root.tagName.toLowerCase() !== 'svg') return;

          const svg = document.importNode(root, true) as unknown as SVGElement;
          sanitizeInlineSvgRoot(svg);

          const w = img.getAttribute('width');
          const h = img.getAttribute('height');
          if (w) svg.setAttribute('width', w);
          if (h) svg.setAttribute('height', h);

          svg.setAttribute('aria-hidden', 'true');
          svg.setAttribute('focusable', 'false');

          img.replaceWith(svg);
          box.dataset.inlineSvgInjected = 'true';
          injected = true;
        } catch {
          // оставляем img — анимация контуров недоступна
        } finally {
          if (!injected && box.querySelector(':scope > img')) {
            box.classList.add('home-advantages__img-box--icon-fallback');
          }
        }
      }),
    );
  }

  private initHomeAdvantagesVideo(): void {
    initHomeAdvantagesVideo();
  }

  private initAdvantageIconsStrokeDraw(): void {
    const boxes = document.querySelectorAll<HTMLElement>('.home-advantages [data-advantage-icon]');

    if (!boxes.length) return;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('home-advantages__img-box--visible');
          obs.unobserve(entry.target);
        });
      },
      { threshold: 1 },
    );

    boxes.forEach((box) => observer.observe(box));
  }

  private initAdvantagesAnimation(): void {
    // Находим секцию преимуществ
    this.advantagesSection = document.querySelector(
      `.home-advantages.${AdvantagesAnimationConfig.animateOnScrollClass}`,
    );

    if (!this.advantagesSection) return;

    // Инициализируем начальное состояние
    this.initAdvantagesInitialState();

    // Создаем Intersection Observer с увеличенным threshold
    this.animationObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.hasAnimatedAdvantages) {
            // Запускаем анимацию с задержкой
            this.startAdvantagesAnimation();
            this.hasAnimatedAdvantages = true;

            // Отключаем observer после запуска анимации
            this.animationObserver?.unobserve(entry.target);
          }
        });
      },
      {
        threshold: AdvantagesAnimationConfig.observerThreshold,
        rootMargin: AdvantagesAnimationConfig.observerRootMargin,
      },
    );

    // Начинаем наблюдение
    this.animationObserver.observe(this.advantagesSection);
  }

  private initAdvantagesInitialState(): void {
    if (!this.advantagesSection) return;

    // Устанавливаем начальное состояние для всей секции
    gsap.set(this.advantagesSection, {
      opacity: 0,
      y: 20, // Легкий сдвиг для плавного появления
    });

    // Устанавливаем начальное состояние для всех анимируемых элементов
    const animatedElements = this.advantagesSection.querySelectorAll(
      '.advantages-cards-row .advantage-card, .content-left .image-card, .content-right .feature-card',
    );

    animatedElements.forEach((element) => {
      gsap.set(element, {
        opacity: 0,
        y: AdvantagesAnimationConfig.yOffset,
      });
    });
  }

  private startAdvantagesAnimation(): void {
    if (!this.advantagesSection) return;

    // ФАЗА 1: Появление всей секции с задержкой
    gsap.to(this.advantagesSection, {
      opacity: 1,
      y: 0,
      duration: AdvantagesAnimationConfig.duration,
      delay: AdvantagesAnimationConfig.sectionAppearanceDelay,
      ease: 'power2.out', // Более плавный ease для секции
      onComplete: () => {
        // ФАЗА 2: Последовательная анимация элементов внутри секции
        this.animateAdvantagesElements();
      },
    });
  }

  private animateAdvantagesElements(): void {
    if (!this.advantagesSection) return;

    // Общая задержка перед началом анимации элементов
    const elementsBaseDelay = AdvantagesAnimationConfig.elementsStartDelay;

    // Анимируем карточки первого ряда
    this.animateFirstRowCards(elementsBaseDelay);

    // Анимируем карточку с изображением
    this.animateImageCard(elementsBaseDelay);

    // Анимируем карточки с иконками
    this.animateFeatureCards(elementsBaseDelay);
  }

  private animateFirstRowCards(baseDelay: number): void {
    if (!this.advantagesSection) return;

    const cards = this.advantagesSection.querySelectorAll('.advantages-cards-row .advantage-card');

    let interval = AdvantagesAnimationConfig.firstRowInterval;

    // Увеличиваем интервал для более плавного появления
    if (AdvantagesAnimationConfig.enableSmoothReveal) {
      interval *= AdvantagesAnimationConfig.smoothRevealMultiplier;
    }

    cards.forEach((card, index) => {
      let delay = baseDelay + AdvantagesAnimationConfig.firstRowStartDelay + index * interval;

      if (AdvantagesAnimationConfig.useDataAttributes) {
        const customDelay = card.getAttribute('data-animation-delay');
        if (customDelay) {
          delay = baseDelay + parseFloat(customDelay);
        }
      }

      this.animateCard(card as HTMLElement, delay);
    });
  }

  private animateImageCard(baseDelay: number): void {
    if (!this.advantagesSection) return;

    const imageCard = this.advantagesSection.querySelector('.content-left .image-card');
    if (!imageCard) return;

    let delay = baseDelay + AdvantagesAnimationConfig.imageCardDelay;

    // Добавляем дополнительную задержку для плавности
    if (AdvantagesAnimationConfig.enableSmoothReveal) {
      delay += AdvantagesAnimationConfig.groupDelayIncrement;
    }

    if (AdvantagesAnimationConfig.useDataAttributes) {
      const customDelay = imageCard.getAttribute('data-animation-delay');
      if (customDelay) {
        delay = baseDelay + parseFloat(customDelay);
      }
    }

    this.animateCard(imageCard as HTMLElement, delay);
  }

  private animateFeatureCards(baseDelay: number): void {
    if (!this.advantagesSection) return;

    const cards = this.advantagesSection.querySelectorAll('.content-right .feature-card');
    const isMobile = window.innerWidth <= AdvantagesAnimationConfig.mobileBreakpoint;

    let interval = isMobile
      ? AdvantagesAnimationConfig.mobileFeatureCardsInterval
      : AdvantagesAnimationConfig.featureCardsInterval;

    // Увеличиваем интервал для более плавного появления
    if (AdvantagesAnimationConfig.enableSmoothReveal) {
      interval *= AdvantagesAnimationConfig.smoothRevealMultiplier;
    }

    let startDelay = AdvantagesAnimationConfig.featureCardsStartDelay;

    // Увеличиваем стартовую задержку для правой колонки
    if (AdvantagesAnimationConfig.enableSmoothReveal) {
      startDelay += AdvantagesAnimationConfig.groupDelayIncrement * 2;
    }

    cards.forEach((card, index) => {
      let delay = baseDelay + startDelay + index * interval;

      if (AdvantagesAnimationConfig.useDataAttributes) {
        const customDelay = card.getAttribute('data-animation-delay');
        if (customDelay) {
          delay = baseDelay + parseFloat(customDelay);
        }
      }

      this.animateCard(card as HTMLElement, delay);
    });
  }

  private animateCard(card: HTMLElement, delay: number): void {
    gsap.to(card, {
      opacity: 1,
      y: 0,
      duration: AdvantagesAnimationConfig.duration,
      delay: delay,
      ease: AdvantagesAnimationConfig.ease,
      onComplete: () => {
        if (AdvantagesAnimationConfig.visibleClass) {
          card.classList.add(AdvantagesAnimationConfig.visibleClass);
        }
      },
    });
  }

  private initHomePromoTitleWords(): void {
    const box = document.querySelector<HTMLElement>('.home-promo__title-box');
    if (!box) return;
    const root = box.closest<HTMLElement>('.home-promo');
    const existingWords = box.querySelectorAll<HTMLElement>('.home-promo__word');
    if (existingWords.length) {
      root?.style.setProperty('--home-promo-words-count', String(existingWords.length));
      box.classList.add('home-promo__title-box--ready');
      return;
    }

    const baseClass = 'home-promo__word';
    let wordIndex = 0;

    const wrapHeading = (heading: HTMLElement): void => {
      const fragment = document.createDocumentFragment();
      for (const node of Array.from(heading.childNodes)) {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as Element;
          if (el.tagName === 'BR') {
            fragment.appendChild(node.cloneNode(true));
            continue;
          }
        }
        if (node.nodeType !== Node.TEXT_NODE) {
          continue;
        }
        const text = node.textContent ?? '';
        const words = text
          .trim()
          .split(/\s+/)
          .filter((w) => w.length > 0);
        if (!words.length) {
          continue;
        }
        words.forEach((word, i) => {
          if (i > 0) {
            fragment.appendChild(document.createTextNode(' '));
          }
          const span = document.createElement('span');
          const idx = wordIndex;
          wordIndex += 1;
          span.className = `${baseClass} ${baseClass}--${idx}`;
          span.style.setProperty('--home-promo-word-index', String(idx));
          span.textContent = word;
          fragment.appendChild(span);
        });
      }
      heading.replaceChildren(fragment);
    };

    box.querySelectorAll<HTMLElement>('h1, h2').forEach((h) => wrapHeading(h));
    root?.style.setProperty('--home-promo-words-count', String(Math.max(wordIndex, 1)));
    box.classList.add('home-promo__title-box--ready');
  }

  private initCursorTrailSparkles(): void {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    document.querySelectorAll<HTMLElement>('.cursor-trail-sparkles-host').forEach((root) => {
      const trail = root.querySelector<HTMLElement>('.cursor-trail-sparkles');
      if (!trail) return;

      let lastSpawn = 0;
      const throttleMs = 14;
      let lastX: number | null = null;
      let lastY: number | null = null;

      let cursorX = 0;
      let cursorY = 0;
      let lastPointerMoveTime = 0;
      let pointerInside = false;
      let idleTimer: number | null = null;
      /** Не дублировать idle, пока идут события движения (см. idleTick). */
      const idleMoveQuietMs = 14;
      const idleIntervalMs = 28;

      const maxParticlesPerTick = 28;

      const syncCursor = (e: PointerEvent): void => {
        const rect = root.getBoundingClientRect();
        cursorX = e.clientX - rect.left;
        cursorY = e.clientY - rect.top;
        lastPointerMoveTime = performance.now();
      };

      const spawnAt = (
        x: number,
        y: number,
        opts?: { jitter?: number; opacityMul?: number; sizeMul?: number },
      ): void => {
        const jitter = opts?.jitter ?? 36;
        const opacityMul = opts?.opacityMul ?? 1;
        const sizeMul = opts?.sizeMul ?? 1;
        const jx = (Math.random() - 0.5) * jitter;
        const jy = (Math.random() - 0.5) * jitter;

        const particle = document.createElement('span');
        particle.className = 'cursor-trail-sparkles__particle';
        particle.style.left = `${x + jx}px`;
        particle.style.top = `${y + jy}px`;
        const sizePx = (4 + Math.random() * 5) * sizeMul;
        const opacity = (0.55 + Math.random() * 0.28) * opacityMul;
        particle.style.setProperty('--sparkle-s', `${sizePx}px`);
        particle.style.setProperty('--sparkle-o', String(Math.min(1, opacity)));
        trail.appendChild(particle);

        particle.addEventListener(
          'animationend',
          () => {
            particle.remove();
          },
          { once: true },
        );
      };

      const idleTick = (): void => {
        if (!pointerInside) return;
        if (performance.now() - lastPointerMoveTime < idleMoveQuietMs) return;

        for (let i = 0; i < 2; i += 1) {
          spawnAt(cursorX, cursorY, {
            jitter: 36,
            opacityMul: 0.95,
            sizeMul: 1,
          });
        }
      };

      const onPointerMove = (e: PointerEvent): void => {
        syncCursor(e);

        const now = performance.now();
        if (now - lastSpawn < throttleMs) return;
        lastSpawn = now;

        const baseX = cursorX;
        const baseY = cursorY;

        let spawned = 0;

        if (lastX !== null && lastY !== null) {
          const dx = baseX - lastX;
          const dy = baseY - lastY;
          const dist = Math.hypot(dx, dy);

          if (dist > 3) {
            const samples = Math.min(12, Math.max(2, Math.ceil(dist / 10)));

            for (let i = 0; i < samples && spawned < maxParticlesPerTick; i += 1) {
              const t = (i + 1) / (samples + 1);
              const px = lastX + dx * t;
              const py = lastY + dy * t;
              const focus = t * t;
              const count = focus < 0.2 ? 0 : focus < 0.55 ? 1 : 2;

              for (let c = 0; c < count && spawned < maxParticlesPerTick; c += 1) {
                spawnAt(px, py, {
                  jitter: 14 * (1 - focus) + 6,
                  opacityMul: 0.28 + 0.72 * focus,
                  sizeMul: 0.55 + 0.45 * focus,
                });
                spawned += 1;
              }
            }
          }
        }

        const headCount = Math.min(4, maxParticlesPerTick - spawned);
        for (let h = 0; h < headCount; h += 1) {
          spawnAt(baseX, baseY, {
            jitter: 36,
            opacityMul: 1,
            sizeMul: 1,
          });
        }

        lastX = baseX;
        lastY = baseY;
      };

      const onPointerEnter = (e: PointerEvent): void => {
        pointerInside = true;
        syncCursor(e);
        if (idleTimer === null) {
          idleTimer = window.setInterval(idleTick, idleIntervalMs);
        }
      };

      const onPointerLeave = (): void => {
        pointerInside = false;
        lastX = null;
        lastY = null;
        if (idleTimer !== null) {
          window.clearInterval(idleTimer);
          idleTimer = null;
        }
      };

      root.addEventListener('pointerenter', onPointerEnter);
      root.addEventListener('pointermove', onPointerMove);
      root.addEventListener('pointerleave', onPointerLeave);
    });
  }

  private bindEvents(): void {
    this.indicators.forEach((indicator, index) => {
      indicator.addEventListener('click', () => {
        this.setActiveIndicator(index);
      });
    });

    if (this.carousel) {
      this.carousel.addEventListener('slide.bs.carousel', (event: Event) => {
        const bsEvent = event as unknown as { to: number };
        this.setActiveIndicator(bsEvent.to);
      });
    }

    this.setActiveIndicator(0);
  }

  private setActiveIndicator(activeIndex: number): void {
    this.indicators.forEach((indicator, index) => {
      if (index === activeIndex) {
        indicator.classList.add('active');
      } else {
        indicator.classList.remove('active');
      }
    });
  }
}

registerPageInit('index', (root?: ParentNode) => {
  const page = new Index();
  page.init(root);
});
